'use client';

import { useEffect, useRef } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useTour as useReactourTour } from '@reactour/tour';
import { useTour } from '@/lib/tour-context';
import { getTourConfig, type TourId } from '@/lib/tours';
import { logger } from '@/lib/logger';
import { useModalState } from '@/lib/modal-context';

/**
 * Single TourManager component that handles all tours
 * According to @reactour/tour docs, there's only one tour instance
 * We use setSteps() to dynamically change which tour is active
 */
export function TourManager() {
  const { activeTour, completeTour, skipTour } = useTour();
  const { setIsOpen, setSteps, setCurrentStep, isOpen, currentStep, steps } = useReactourTour();
  const { closeModal } = useModalState();
  const router = useRouter();
  const pathname = usePathname();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);

  // Handle active tour changes - this is the main tour controller
  useEffect(() => {
    // Clear any existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    retryCountRef.current = 0;

    if (!activeTour) {
      // No active tour - close if open and close any open modals
      if (isOpen) {
        setIsOpen(false);
      }
      // Close any open modals when no tour is active
      closeModal('shock-parameters');
      closeModal('borrowing-instruments');
      return;
    }

    // Close any open modals before starting a new tour
    // This ensures modals from previous tours don't persist
    const isModalTour = activeTour === 'onboarding-shock-parameters' || activeTour === 'onboarding-borrowing-instruments';
    if (!isModalTour) {
      // If the new tour is not a modal tour, close all modals
      logger.info('Closing modals before starting non-modal tour', { activeTour });
      closeModal('shock-parameters');
      closeModal('borrowing-instruments');
    } else {
      // If the new tour is a modal tour, close the other modal (if different)
      if (activeTour === 'onboarding-shock-parameters') {
        closeModal('borrowing-instruments');
      } else if (activeTour === 'onboarding-borrowing-instruments') {
        closeModal('shock-parameters');
      }
    }

    // Don't restart tour if it was completed manually (we're navigating away)
    if (typeof window !== 'undefined' && sessionStorage.getItem('tour-completed-manually') === 'true') {
      return;
    }

    const config = getTourConfig(activeTour);
    if (!config) {
      logger.warn('Tour config not found', { activeTour });
      return;
    }

    // Map tour IDs to their required pages
    const tourPages: Record<TourId, string | null> = {
      'first-time-user': null,
      'onboarding-scenario-detail': '/modules/scenario-analysis',
      'onboarding-scenario-comparison': '/modules/scenario-analysis',
      'onboarding-shock-parameters': '/modules/scenario-analysis', // Changed: requires scenario analysis page
      'onboarding-borrowing-instruments': '/modules/scenario-analysis', // Changed: requires scenario analysis page
      'onboarding-stochastic-projection': '/modules/stochastic-projection',
    };

    const requiredPage = tourPages[activeTour];

    // If tour requires a specific page and we're not on it, navigate first
    if (requiredPage && pathname !== requiredPage) {
      logger.info('Navigating to required page for tour', { activeTour, requiredPage });
      router.push(requiredPage);
      // Tour will start after navigation completes (this effect will run again)
      return;
    }

    // For modal-based tours, we start at step 0 (button) and open modal when advancing to step 1
    // Note: isModalTour check is handled in TourProviderInner, not needed here
    
    // Start at step 0 (button) for all tours - modal will be opened when advancing to step 1
    const stepIndex = 0;
    const firstStepSelector = config.steps[stepIndex]?.selector;
    const MAX_RETRIES = 50; // 5 seconds max wait
    const initialDelay = 500;

    const checkElement = () => {
      try {
        // Handle selector which can be string or Element
        const selectorString = typeof firstStepSelector === 'string' ? firstStepSelector : null;
        const element = selectorString ? document.querySelector(selectorString) : (firstStepSelector instanceof Element ? firstStepSelector : null);
        if (element || !firstStepSelector || (typeof firstStepSelector === 'string' && firstStepSelector === 'body')) {
          // Element found or no selector needed - set up the tour
          if (setSteps && setCurrentStep && setIsOpen) {
            setSteps(config.steps);
            setCurrentStep(stepIndex); // Start at the appropriate step
            setTimeout(() => {
              setIsOpen(true);
              logger.info('Tour opened', { activeTour, selector: firstStepSelector, stepIndex });
            }, 300);
          }
        } else {
          // Element not found yet, retry
          retryCountRef.current++;
          if (retryCountRef.current < MAX_RETRIES) {
            timeoutRef.current = setTimeout(checkElement, 100);
          } else {
            // Max retries reached, try to open anyway
            logger.warn('Max retries reached, opening tour anyway', { activeTour, selector: firstStepSelector });
            if (setSteps && setCurrentStep && setIsOpen) {
              setSteps(config.steps);
              setCurrentStep(stepIndex);
              setTimeout(() => {
                setIsOpen(true);
                logger.info('Tour opened (max retries fallback)', { activeTour, stepIndex });
              }, 300);
            }
          }
        }
      } catch (error) {
        logger.warn('Error checking for tour element', { error, selector: firstStepSelector });
        // Fallback: open tour anyway
        if (setSteps && setCurrentStep && setIsOpen) {
          setSteps(config.steps);
          setCurrentStep(stepIndex);
          setTimeout(() => {
            setIsOpen(true);
            logger.info('Tour opened (error fallback)', { activeTour, stepIndex });
          }, 500);
        }
      }
    };

    // Initial delay to allow page/modal to render
    timeoutRef.current = setTimeout(checkElement, initialDelay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [activeTour, pathname, router, setSteps, setCurrentStep, setIsOpen, isOpen, closeModal]);

  // Track if tour was actually opened (not just navigating)
  const tourWasOpenedRef = useRef(false);

  // Track when tour opens
  useEffect(() => {
    if (activeTour && isOpen) {
      tourWasOpenedRef.current = true;
    }
  }, [activeTour, isOpen]);


  // Handle tour close/completion
  // React 19 Best Practice: Proper cleanup of timeouts and correct dependency array
  useEffect(() => {
    // Only handle completion if tour was actually opened and then closed
    // Don't mark as skipped if we're just navigating (tour never opened)
    // Don't restart if tour was completed manually
    if (activeTour && !isOpen && steps && steps.length > 0 && currentStep !== undefined && tourWasOpenedRef.current) {
      // Don't handle completion if tour was completed manually (we're navigating away)
      if (typeof window !== 'undefined' && sessionStorage.getItem('tour-completed-manually') === 'true') {
        return;
      }
      
      // Small delay to avoid race conditions during navigation
      const timeoutId = setTimeout(() => {
        // Check if tour is still closed (not just navigating)
        if (!isOpen && activeTour) {
          // Tour was closed - check if it was completed or skipped
          if (currentStep === steps.length - 1) {
            // Tour was completed - mark as complete
            // Set flag to prevent restart
            sessionStorage.setItem('tour-completed-manually', 'true');
            completeTour(activeTour);
            tourWasOpenedRef.current = false;
            
            // Navigate back to home page (getting started checklist) after tour completion
            // Use a small delay to ensure tour completion is processed first
            setTimeout(() => {
              if (pathname !== '/') {
                logger.info('Navigating to home page after tour completion', { activeTour, currentPath: pathname });
                router.push('/');
              }
              // Clear the flag after navigation
              setTimeout(() => {
                sessionStorage.removeItem('tour-completed-manually');
              }, 500);
            }, 300);
          } else if (currentStep >= 0) {
            // Only skip if we actually started the tour (currentStep >= 0)
            skipTour(activeTour);
            tourWasOpenedRef.current = false;
            
            // Also navigate to home page after skipping tour
            setTimeout(() => {
              if (pathname !== '/') {
                logger.info('Navigating to home page after tour skip', { activeTour, currentPath: pathname });
                router.push('/');
              }
            }, 300);
          }
        }
      }, 200);

      return () => clearTimeout(timeoutId);
    } else if (!activeTour) {
      // Reset flag when no active tour
      tourWasOpenedRef.current = false;
    }
  }, [activeTour, isOpen, currentStep, steps, completeTour, skipTour, pathname, router, closeModal]);

  // Close modals when tour completes or closes
  useEffect(() => {
    if (!activeTour && !isOpen) {
      // Tour is closed and no active tour - close any open modals
      logger.info('Closing modals after tour closed', { activeTour, isOpen });
      closeModal('shock-parameters');
      closeModal('borrowing-instruments');
    }
  }, [activeTour, isOpen, closeModal]);

  return null;
}

/**
 * Hook to start a tour programmatically
 * Simply sets the active tour - TourManager will handle the rest
 */
export function useStartTour() {
  const { startTour } = useTour();

  return (tourId: TourId) => {
    logger.info('Starting tour', { tourId });
    startTour(tourId);
    // TourManager will handle navigation, modal opening, and tour setup
  };
}

